import React from 'react';

import { AttrsHelper } from '../../../sb-helpers/attrs-helper';

export type GroupedElementsItemProps = {
  children?: React.ReactNode;
  /**
   * Takes the space the other items leave, and lets its own content clip inside it - the pair a
   * label too long for its segment needs.
   *
   * **The main axis, which is what flex means by it.** In a horizontal run that is the width; in a
   * vertical one it is the height, and a label there is the caller's problem to solve with an
   * `IconButton` rather than this prop's to guess at. Only meaningful inside a `fluid` group: a
   * shrink-wrapped one has no spare space to hand out.
   */
  fluid?: boolean;
  name?: string;
  /**
   * Sits this segment **on** the one before it rather than beside it: it keeps its width but gives
   * the same amount back, so it takes no room in the run. The segment before it therefore has the
   * whole width while nothing is reaching for the group, and opens a gutter only on hover or focus
   * so its content stops clear of what is now painted over it.
   *
   * For an action at the end of a row - a menu, a dismiss - that should not cost the row's content
   * any width it is not currently using. Horizontal runs only.
   */
  overlay?: boolean;
  /**
   * Holds the gutter `overlay` opens, without waiting for a pointer - for an overlay whose content is
   * showing on its own account rather than because the row is being reached for.
   *
   * A menu's trigger fades in on hover and needs none of this; something standing in its place that
   * is visible from the start - a spinner while the row is busy - does, or the content it is painted
   * over runs underneath it. Only meaningful alongside `overlay`.
   */
  revealed?: boolean;
};

/**
 * One segment of a `GroupedElements`.
 *
 * **It exists so the group can size its segments without reaching into what they hold.** Whatever a
 * caller puts inside - a `Button`, an `IconButton`, a `ContextMenu`, a `Tooltip`'s anchor - keeps
 * every prop it was given; the box that grows, clips and squares off at a join is this one.
 */
export const GroupedElementsItem = ({
  children,
  fluid = false,
  name,
  overlay = false,
  revealed = false,
}: GroupedElementsItemProps) => (
  <div
    className={AttrsHelper.formatClassname(
      'grouped-elements-item',
      fluid && 'grouped-elements-item-fluid',
      overlay && 'grouped-elements-item-overlay',
      revealed && 'grouped-elements-item-revealed'
    )}
    data-test={name}
  >
    {children}
  </div>
);
